import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  LogOut,
  Trash2,
  Settings as SettingsIcon,
  Mail,
  RefreshCw,
  Save,
  Loader2,
  Check,
  Layout as LayoutIcon,
  Palette,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import ContentEditor from "../../components/admin/ContentEditor";
import ThemeEditor from "../../components/admin/ThemeEditor";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("submissions");
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubs, setLoadingSubs] = useState(true);
  const [settings, setSettings] = useState({
    recipient_email: "",
    notify_enabled: true,
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const loadSubmissions = async () => {
    setLoadingSubs(true);
    try {
      const res = await api.get("/admin/submissions");
      setSubmissions(res.data);
    } catch {
      toast.error("Не удалось загрузить заявки");
    } finally {
      setLoadingSubs(false);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await api.get("/admin/settings");
      setSettings(res.data);
    } catch {
      toast.error("Не удалось загрузить настройки");
    }
  };

  useEffect(() => {
    loadSubmissions();
    loadSettings();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Удалить заявку?")) return;
    try {
      await api.delete(`/admin/submissions/${id}`);
      setSubmissions((prev) => prev.filter((s) => s.id !== id));
      toast.success("Заявка удалена");
    } catch {
      toast.error("Не удалось удалить");
    }
  };

  const markDone = async (id) => {
    try {
      await api.patch(`/admin/submissions/${id}`, { status: "done" });
      setSubmissions((prev) =>
        prev.map((s) => (s.id === id ? { ...s, status: "done" } : s))
      );
    } catch {
      toast.error("Не удалось обновить статус");
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      await api.put("/admin/settings", settings);
      toast.success("Настройки сохранены");
    } catch {
      toast.error("Не удалось сохранить");
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <div className="min-h-screen bg-midnight text-white">
      <header className="bg-midnight-2 border-b border-white/10 sticky top-0 z-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="font-display font-extrabold tracking-tight text-white text-[16px]">
              DIYATOUR
            </div>
            <span className="font-mono text-[10px] tracking-[0.25em] text-coral uppercase">
              Admin
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block font-mono text-[11px] tracking-widest uppercase text-white/50">
              {user?.username}
            </span>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[11px] tracking-widest uppercase hover:border-coral hover:text-coral transition-colors"
            >
              <LogOut size={13} /> Выйти
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-10">
        <div className="overline mb-3">DASHBOARD · 仪表板</div>
        <h1 className="font-display font-extrabold tracking-tighter text-white text-4xl md:text-5xl mb-10 uppercase">
          Заявки
        </h1>

        <div className="flex items-center gap-2 border-b border-white/10 mb-8 overflow-x-auto">
          {[
            { id: "submissions", label: "Заявки", icon: Mail },
            { id: "content", label: "Контент сайта", icon: LayoutIcon },
            { id: "theme", label: "Цвета", icon: Palette },
            { id: "settings", label: "Email", icon: SettingsIcon },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-[11px] tracking-widest uppercase border-b-2 -mb-px transition-colors ${
                tab === id
                  ? "border-coral text-white"
                  : "border-transparent text-white/45 hover:text-white"
              }`}
            >
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>

        {tab === "submissions" && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div className="font-mono text-[12px] text-white/55">
                Всего:{" "}
                <span className="text-white font-bold">{submissions.length}</span>
              </div>
              <button
                onClick={loadSubmissions}
                data-testid="admin-refresh"
                className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[11px] tracking-widest uppercase hover:border-coral hover:text-coral transition-colors"
              >
                <RefreshCw size={13} /> Обновить
              </button>
            </div>

            {loadingSubs ? (
              <div className="bg-midnight-2 border border-white/10 p-16 text-center text-white/40 font-mono text-sm">
                Loading…
              </div>
            ) : submissions.length === 0 ? (
              <div className="bg-midnight-2 border border-white/10 p-16 text-center text-white/40 font-mono text-sm">
                Заявок пока нет.
              </div>
            ) : (
              <div className="bg-midnight-2 border border-white/10 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="admin-submissions-table">
                    <thead>
                      <tr className="border-b border-white/10 bg-midnight-3">
                        {["Имя", "Контакт", "Тип", "Комментарий", "Дата", "Статус", ""].map(
                          (h) => (
                            <th
                              key={h}
                              className="text-left p-4 font-mono text-[10px] tracking-[0.2em] uppercase text-white/45"
                            >
                              {h}
                            </th>
                          )
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {submissions.map((s) => (
                        <tr
                          key={s.id}
                          className="border-b border-white/5 hover:bg-midnight-3"
                        >
                          <td className="p-4 font-display font-bold text-white">
                            {s.name}
                          </td>
                          <td className="p-4 font-mono text-[12px] text-white/80">
                            {s.contact}
                          </td>
                          <td className="p-4 text-sm text-white/80">{s.tour_type}</td>
                          <td className="p-4 text-sm text-white/65 max-w-xs truncate">
                            {s.comment || "—"}
                          </td>
                          <td className="p-4 font-mono text-[11px] text-white/45 whitespace-nowrap">
                            {new Date(s.created_at).toLocaleString("ru-RU")}
                          </td>
                          <td className="p-4">
                            <span
                              className={`font-mono text-[10px] tracking-widest uppercase px-2 py-1 ${
                                s.status === "done"
                                  ? "bg-coral text-midnight"
                                  : "bg-coral/20 text-coral"
                              }`}
                            >
                              {s.status}
                            </span>
                          </td>
                          <td className="p-4 flex items-center gap-2">
                            {s.status !== "done" && (
                              <button
                                data-testid={`admin-mark-done-${s.id}`}
                                onClick={() => markDone(s.id)}
                                className="p-2 hover:bg-coral hover:text-midnight transition-colors"
                                title="Отметить выполненной"
                              >
                                <Check size={14} />
                              </button>
                            )}
                            <button
                              data-testid={`admin-delete-${s.id}`}
                              onClick={() => remove(s.id)}
                              className="p-2 hover:bg-brand-red hover:text-white transition-colors"
                              title="Удалить"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "content" && <ContentEditor />}
        {tab === "theme" && <ThemeEditor />}

        {tab === "settings" && (
          <form
            onSubmit={saveSettings}
            className="bg-midnight-2 border border-white/10 p-8 max-w-2xl"
            data-testid="admin-settings-form"
          >
            <div className="overline mb-3">EMAIL · 邮件</div>
            <h2 className="font-display font-extrabold text-white text-2xl mb-6 uppercase">
              Email для уведомлений
            </h2>
            <p className="text-white/65 text-sm mb-8 leading-relaxed">
              Укажите email, на который будут приходить уведомления о новых заявках.
              Если в окружении не настроен ключ Resend API, уведомления отправляться не
              будут (заявки всё равно сохраняются в БД).
            </p>

            <div className="space-y-6">
              <div>
                <label className="overline mb-2 block">Email получателя</label>
                <input
                  data-testid="admin-recipient-email"
                  type="email"
                  required
                  value={settings.recipient_email}
                  onChange={(e) =>
                    setSettings({ ...settings, recipient_email: e.target.value })
                  }
                  className="w-full bg-midnight-3 border border-white/10 focus:border-coral px-4 py-3 text-white outline-none font-display transition-colors"
                />
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  data-testid="admin-notify-enabled"
                  type="checkbox"
                  checked={settings.notify_enabled}
                  onChange={(e) =>
                    setSettings({ ...settings, notify_enabled: e.target.checked })
                  }
                  className="w-4 h-4 accent-coral"
                />
                <span className="font-display text-white">
                  Включить уведомления по email
                </span>
              </label>
            </div>

            <button
              type="submit"
              data-testid="admin-save-settings"
              disabled={savingSettings}
              className="btn-primary !py-4 !px-7 mt-8 disabled:opacity-50"
            >
              {savingSettings ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Сохранение...
                </>
              ) : (
                <>
                  <Save size={14} /> Сохранить
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
